import React, { useState } from "react";
import { MenuItem, TextField } from "@material-ui/core";
import { DialogActions, DialogBody, DialogBox, DialogOverlay, DialogTitle } from "../styles";
import { LABELS } from "../labels";
import { countFindMatches, countFindMatchesBreakdown, getEffectiveText } from "../selectors";
import type { FindReplaceState, StationsTextEntry } from "../types";

interface Props {
	open: boolean;
	state: FindReplaceState;
	entries: StationsTextEntry[];
	filters: { screen: string; group: string };
	selectionSize: number;
	onStateChange: (patch: Partial<FindReplaceState>) => void;
	onApply: (params: {
		find: string;
		replace: string;
		scope: FindReplaceState["scope"];
		matchMode: FindReplaceState["matchMode"];
	}) => number;
	/** Close dialog and filter the main table to rows matching `find`. */
	onJumpToMatches: (find: string) => void;
	onClose: () => void;
}

export default function FindReplaceDialog({
	open,
	state,
	entries,
	filters,
	selectionSize,
	onStateChange,
	onApply,
	onJumpToMatches,
	onClose,
}: Props) {
	const [lastCount, setLastCount] = useState<number | null>(null);

	if (!open) return null;

	const candidateEntries =
		state.scope === "screen" && filters.screen
			? entries.filter((e) => e.screen === filters.screen)
			: state.scope === "group" && filters.group
			? entries.filter((e) => e.group === filters.group)
			: state.scope === "selected"
			? entries
			: entries;

	const matchCount = state.find ? countFindMatches(candidateEntries, state.find, state.matchMode) : 0;
	const matchKeys = state.find
		? new Set(
				candidateEntries
					.filter((e) => {
						const text = getEffectiveText(e);
						try {
							if (state.matchMode === "regex") return new RegExp(state.find, "i").test(text);
							if (state.matchMode === "wholeword")
								return new RegExp(
									`\\b${state.find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
									"i"
								).test(text);
							return text.toLowerCase().includes(state.find.toLowerCase());
						} catch {
							return false;
						}
					})
					.map((e) => e.key)
		  ).size
		: 0;
	const breakdown = state.find
		? countFindMatchesBreakdown(candidateEntries, state.find, state.matchMode)
		: { overridden: 0, defaults: 0 };

	function handleApply() {
		const count = onApply({
			find: state.find,
			replace: state.replace,
			scope: state.scope,
			matchMode: state.matchMode,
		});
		setLastCount(count);
		onClose();
	}

	return (
		<DialogOverlay onClick={onClose}>
			<DialogBox onClick={(e) => e.stopPropagation()}>
				<DialogTitle>{LABELS.findReplaceTitle}</DialogTitle>

				<div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						label={LABELS.findLabel}
						value={state.find}
						onChange={(e) => onStateChange({ find: e.target.value })}
						autoFocus
					/>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						label={LABELS.replaceLabel}
						value={state.replace}
						onChange={(e) => onStateChange({ replace: e.target.value })}
					/>
					<div style={{ display: "flex", gap: 10 }}>
						<TextField
							select
							fullWidth
							variant="outlined"
							size="small"
							label={LABELS.matchMode}
							value={state.matchMode}
							onChange={(e) =>
								onStateChange({ matchMode: e.target.value as FindReplaceState["matchMode"] })
							}>
							<MenuItem value="substring">{LABELS.matchSubstring}</MenuItem>
							<MenuItem value="wholeword">{LABELS.matchWholeWord}</MenuItem>
							<MenuItem value="regex">{LABELS.matchRegex}</MenuItem>
						</TextField>
						<TextField
							select
							fullWidth
							variant="outlined"
							size="small"
							label="Scope"
							value={state.scope}
							onChange={(e) => onStateChange({ scope: e.target.value as FindReplaceState["scope"] })}>
							<MenuItem value="all">{LABELS.scopeAll}</MenuItem>
							<MenuItem
								value="screen"
								disabled={!filters.screen}>
								{LABELS.scopeScreen}
							</MenuItem>
							<MenuItem
								value="group"
								disabled={!filters.group}>
								{LABELS.scopeGroup}
							</MenuItem>
							<MenuItem
								value="selected"
								disabled={selectionSize === 0}>
								{LABELS.scopeSelected}
							</MenuItem>
						</TextField>
					</div>

					{state.find && (
						<div style={{ fontSize: "0.8125rem", color: "#475569" }}>
							<p style={{ margin: 0 }}>{LABELS.matchPreview(matchCount, matchKeys)}</p>
							{breakdown.defaults > 0 && (
								<p style={{ margin: "4px 0 0", color: "#b45309" }}>
									{breakdown.overridden} đã override, {breakdown.defaults} sẽ được promote từ DEFAULT
									thành override khi Apply.
								</p>
							)}
						</div>
					)}
				</div>

				<DialogActions>
					<button
						onClick={onClose}
						style={{
							padding: "8px 16px",
							border: "1px solid #e2e8f0",
							borderRadius: 8,
							background: "#fff",
							color: "#374151",
							fontSize: "0.875rem",
							cursor: "pointer",
						}}>
						{LABELS.cancelFindReplace}
					</button>
					<button
						onClick={() => onJumpToMatches(state.find)}
						disabled={!state.find || matchCount === 0}
						style={{
							padding: "8px 16px",
							border: "1px solid #2563eb",
							borderRadius: 8,
							background: "#fff",
							color: !state.find || matchCount === 0 ? "#94a3b8" : "#2563eb",
							fontSize: "0.875rem",
							fontWeight: 600,
							cursor: !state.find || matchCount === 0 ? "not-allowed" : "pointer",
						}}>
						Hiển thị trong bảng
					</button>
					<button
						onClick={handleApply}
						disabled={!state.find || matchCount === 0}
						style={{
							padding: "8px 20px",
							border: "none",
							borderRadius: 8,
							background: !state.find || matchCount === 0 ? "#94a3b8" : "#2563eb",
							color: "#fff",
							fontSize: "0.875rem",
							fontWeight: 600,
							cursor: !state.find || matchCount === 0 ? "not-allowed" : "pointer",
						}}>
						{LABELS.applyFindReplace}
					</button>
				</DialogActions>
			</DialogBox>
		</DialogOverlay>
	);
}
